import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, TextInput, ScrollView } from 'react-native';
import { DataStore } from 'aws-amplify';
import { Cooperation, UserGroup, UserType, GroupConversation, Conversation } from '../../models';
import { MaterialIcons } from '@expo/vector-icons';
import * as AppConst from '../../components/AppConst';
import * as CmnUtil from '../../components/CmnUtil';
import IconImages from '../../components/IconImages';

/** ================================
*  アカウント操作画面
*  ================================*/
const UpdateGroup = ({ closeFunction, groupId }: { closeFunction: Function, groupId: string }) => {
  // 組織情報
  interface SelectGroup {
    userGroup: UserGroup;
    cooperation: boolean;
  }

  // 操作種別
  const controlType = {
    none: 'none',
    changeName: 'changeName',
    add: 'add',
    drop: 'drop',
  }

  // 対象組織情報
  const [groupInfo, setGroupInfo] = useState<UserGroup | undefined>(undefined);

  // 協力組織情報
  const [groupList, setGroupList] = useState<SelectGroup[]>([]);

  // 組織名入力
  const [newGroupName, setNewGroupName] = useState('');

  // 検索ワード
  const [findWord, setFindWord] = useState('');

  // 操作組織
  const [targetGroup, setTargetGroup] = useState<UserGroup | undefined>(undefined);

  // 操作
  const [control, setControl] = useState<string>(controlType.none);

  useEffect(() => {
    (async () => {
      // 組織情報取得
      const userGroup = (await DataStore.query(UserGroup, groupId));
      setGroupInfo(userGroup);
      if (userGroup == undefined) {
        setGroupList([]);
        return;
      }

      // 協力組織一覧
      findGroup(userGroup);
    })();
  }, []);

  // 組織検索
  const findGroup = async (tmpUserGroup: UserGroup) => {
    // 組織一覧
    const cooperationTo = (await DataStore.query(Cooperation, c => c.groupID('eq', groupId)));

    // 組織覧
    // 生産者と普及指導員の関係だけ協力関係にできるので、対象組織と逆の組織を検索する。
    const findType = tmpUserGroup.groupType == UserType.PRODUCER ? UserType.INSTRUCTOR : UserType.PRODUCER;
    const groups = (await DataStore.query(UserGroup, c => c.id('ne', groupId).groupType('eq', findType).groupName('contains', findWord)));

    // 協力組織の一覧と協力組織以外の一覧作成
    const tmpGroupList = [];
    for (const group of groups) {
      let i = 0;
      for (i = 0; i < cooperationTo.length; i++) {
        const cooperation = cooperationTo[i];
        if (group.id == cooperation.cooperationGroupID) {
          tmpGroupList.push({ userGroup: group, cooperation: true });
          break;
        }
      }
      if (i >= cooperationTo.length) {
        tmpGroupList.push({ userGroup: group, cooperation: false });
      }
    }
    setGroupList([...tmpGroupList]);
  }

  // 組織名変更
  const editGroupName = async () => {
    CmnUtil.startLoading();
    // エラーチェック
    if (newGroupName.length == 0) {
      CmnUtil.appAlert('組織名は必須入力です。');
      CmnUtil.endLoading();
      setControl(controlType.none);
      return;
    }

    const userGroup = (await DataStore.query(UserGroup, groupId));
    if (!userGroup) {
      CmnUtil.appAlert('既に削除されている組織です。');
      CmnUtil.endLoading();
      setControl(controlType.none);
      return;
    }

    // 有効化/無効化
    await DataStore.save(
      UserGroup.copyOf(userGroup, update => { update.groupName = newGroupName })
    );

    CmnUtil.appMsg('組織名を変更しました。');
    CmnUtil.endLoading();
    setControl(controlType.none);
    closeFunction();
  }

  // 協力関係追加
  const addCooperation = async (tagetGroup: UserGroup) => {
    CmnUtil.startLoading();
    // user -> cooperation
    const cooperationTo = (await DataStore.query(Cooperation, c => c.groupID('eq', groupId).cooperationGroupID('eq', tagetGroup.id)));
    if (cooperationTo.length == 0) {
      await DataStore.save(new Cooperation({
        groupID: groupId,
        cooperationGroupID: tagetGroup.id,
      }));
    }

    // cooperation -> user
    const cooperationFrom = (await DataStore.query(Cooperation, c => c.groupID('eq', tagetGroup.id).cooperationGroupID('eq', groupId)));
    if (cooperationFrom.length == 0) {
      await DataStore.save(new Cooperation({
        groupID: tagetGroup.id,
        cooperationGroupID: groupId,
      }));
    }

    // Conversation
    const conversation = await DataStore.save(new Conversation({}));

    // GroupConversation
    await DataStore.save(new GroupConversation({ group: groupInfo!, conversation: conversation, isIntergroup: true }));
    await DataStore.save(new GroupConversation({ group: tagetGroup, conversation: conversation, isIntergroup: true }));

    CmnUtil.endLoading();
    setControl(controlType.none);

    CmnUtil.appMsg('協力関係を追加しました。');
    findGroup(groupInfo!);
  }

  // 協力関係解除
  const dropCooperation = async (tagetGroup: UserGroup) => {
    CmnUtil.startLoading();

    // Conversation, GroupConversationを削除
    const toGroupConversations = (await DataStore.query(GroupConversation)).filter(c => c.group.id === groupId);
    const fromGroupConversations = (await DataStore.query(GroupConversation)).filter(c => c.group.id === tagetGroup.id);
    for (const toGroupConversation of toGroupConversations) {
      for (const fromGroupConversation of fromGroupConversations) {
        if (toGroupConversation.conversation.id == fromGroupConversation.conversation.id) {
          await DataStore.delete(GroupConversation, c => c.id('eq', toGroupConversation.conversation.id));
          await DataStore.delete(Conversation, c => c.id('eq', toGroupConversation.conversation.id));
        }
      }
    }

    // user -> cooperation
    await DataStore.delete(Cooperation, c => c.groupID('eq', groupId).cooperationGroupID('eq', tagetGroup.id));

    // cooperation -> user
    await DataStore.delete(Cooperation, c => c.groupID('eq', tagetGroup.id).cooperationGroupID('eq', groupId));
    CmnUtil.endLoading();
    setControl(controlType.none);

    CmnUtil.appMsg('協力関係を解除しました。');
    findGroup(groupInfo!);
  }

  return (
    <View style={AppConst.styles.dialog}>
      <View style={AppConst.styles.dialogContents}>
        <View style={{ backgroundColor: AppConst.defaultBaseColor, width: 450, borderRadius: 10 }}>
          <AppConst.DialogHeader closeFunction={closeFunction} />

          <Text style={{ paddingLeft: 10, paddingTop: 10 }}>{groupInfo?.groupName}</Text>

          {/* アカウント操作 */}
          {groupInfo && <View style={{ flexDirection: 'row', paddingTop: 10 }}>
            <TextInput
              style={styles.textInput}
              value={newGroupName}
              onChangeText={setNewGroupName}
              textContentType='none'
              autoCapitalize='none'
              placeholder='新しい組織名を入力してください'
            />
            <TouchableOpacity style={[styles.actionButton, styles.positiveColor]} onPress={() => { setControl(controlType.changeName) }}>
              <Text>組織名変更</Text>
            </TouchableOpacity>
          </View>}

          {/* 協力関係追加/解除 */}
          <View style={{ marginTop: 20, padding: 10, borderTopWidth: 1 }}>
            <Text>協力関係追加/解除</Text>
            <View style={{ marginTop: 10 }}>
              <View style={{ width: '100%', flexDirection: 'row', justifyContent: 'flex-end' }}>
                <TextInput
                  style={{ borderBottomWidth: 1, width: 350, paddingLeft: 5 }}
                  value={findWord}
                  onChangeText={setFindWord}
                  textContentType='none'
                  autoCapitalize='none'
                  placeholder='組織名で検索してリスト表示'
                />
                <TouchableOpacity style={{ borderBottomWidth: 1, width: 30, alignItems: 'center' }} onPress={() => { findGroup(groupInfo!) }}>
                  <MaterialIcons name='search' color={AppConst.defaultColor} size={24} />
                </TouchableOpacity>
              </View>

              <ScrollView style={{ marginTop: 10, width: '100%', height: 300 }}>
                {groupList.map((data: SelectGroup, index: number) => (

                  <View key={index} style={{ backgroundColor: AppConst.defaultBaseInputColor, borderRadius: 10, marginTop: 5, marginBottom: 5 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly' }}>
                      <View style={{ justifyContent: 'center' }}>
                        <CmnUtil.S3Image imageKey={data.userGroup.icon} style={styles.icon} defaultSource={IconImages.ic_group} />
                      </View>
                      <View style={{ paddingLeft: 10, width: '50%' }}>
                        <Text>{data.userGroup.groupName}</Text>
                      </View>
                      {data.cooperation ?
                        <TouchableOpacity style={[styles.actionButton, styles.dangerousColor]} onPress={() => { setTargetGroup(data.userGroup); setControl(controlType.drop) }}>
                          <Text>ー 協力関係解除</Text>
                        </TouchableOpacity>
                        :
                        <TouchableOpacity style={[styles.actionButton, styles.positiveColor]} onPress={() => { setTargetGroup(data.userGroup); setControl(controlType.add) }}>
                          <Text>＋ 協力関係追加</Text>
                        </TouchableOpacity>
                      }
                    </View>
                  </View>
                ))}
              </ScrollView>
            </View>
          </View>

          {/* 確認ダイアログ */}
          {control != controlType.none && <View style={[AppConst.styles.dialog, { borderRadius: 10 }]}>
            <View style={AppConst.styles.dialogContents}>
              <View style={{ backgroundColor: AppConst.defaultBaseColor, width: 300, borderRadius: 10 }}>
                <AppConst.DialogHeader closeFunction={() => { setControl(controlType.none) }} />

                {control == controlType.changeName && <View style={{ padding: 20 }}>
                  <Text>組織名を変更しますか？</Text>
                </View>}
                {control == controlType.add && <View style={{ padding: 20 }}>
                  <Text>{groupInfo?.groupName}と{targetGroup?.groupName}の協力関係を追加しますか？</Text>
                </View>}
                {control == controlType.drop && <View style={{ padding: 20 }}>
                  <Text style={{ color: 'red' }}>危険操作です！！</Text>
                  <Text>解除するとチャット履歴が削除され、復旧できません。</Text>
                  <Text>{groupInfo?.groupName}と{targetGroup?.groupName}の協力関係を解除しますか？</Text>
                </View>}

                <View style={{ flexDirection: 'row', height: 40, borderTopWidth: 1, borderColor: '#707070' }}>
                  <TouchableOpacity style={styles.dialogNo} onPress={() => { setControl(controlType.none) }}>
                    <Text>いいえ</Text>
                  </TouchableOpacity>

                  {control == controlType.changeName && <TouchableOpacity style={styles.dialogYes} onPress={() => { editGroupName() }}>
                    <Text>はい</Text>
                  </TouchableOpacity>}

                  {control == controlType.add && <TouchableOpacity style={styles.dialogYes} onPress={() => { addCooperation(targetGroup!) }}>
                    <Text>はい</Text>
                  </TouchableOpacity>}

                  {control == controlType.drop && <TouchableOpacity style={styles.dialogYes} onPress={() => { dropCooperation(targetGroup!) }}>
                    <Text>はい</Text>
                  </TouchableOpacity>}
                </View>
              </View>
            </View>
          </View>}

        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  actionButton: {
    width: 120,
    height: 30,
    borderWidth: 1,
    marginLeft: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textInput: {
    width: 300,
    borderBottomWidth: 1,
    backgroundColor: AppConst.defaultBaseInputColor,
    marginLeft: 10,
    height: 30,
    paddingLeft: 5,
  },
  icon: {
    width: 50,
    height: 50,
    borderRadius: 25,
  },
  positiveColor: {
    backgroundColor: '#3cb371',
  },
  dangerousColor: {
    backgroundColor: '#ff6347',
  },
  dialogNo: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderRightWidth: 1,
    borderColor: '#707070',
  },
  dialogYes: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default UpdateGroup;
